import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OverlayContext } from "../../../context/OverlayContext";
import { ToastContext } from "../../../context/ToastContext";
import { NavigationContext } from "../../../context/NavigationContext";
import { Table, Typography, Button, Form, List, Tag, Tooltip, Input, TabsProps, Tabs } from "antd";
import { ArrowLeftOutlined, StopOutlined } from "@ant-design/icons";
import UserService from "../../../services/crud/UserService";
// import UserForm from "../components/UserForm";
// import DeleteForm from "../components/DeleteForm";
import User from "../../../models/User";
import UserForm from "../../../components/forms/UserForm";

import * as _ from "lodash";

import BookingsTable, { BookingTableData } from "../../../components/tables/BookingsTable";
import SubscriptionsTable, { GenericSubscriptionsTable } from "../../../components/tables/SubscriptionsTable";
import Subscription from "../../../models/Subscription";
import { ViewTitle } from "../../../components/ViewTitle";
import DescriptionDataList from "../../../components/DescriptionDataList";
import AccountService from "../../../services/crud/AccountService";
import SubscriptionService from "../../../services/crud/SubscriptionService";
import Account from "../../../models/Account";
import { GoBackButton } from "../../../components/GoBackButton";
import { formatDateTime } from "../../../utils/dates";
import { LangKey } from "../../../models/LangKey";
import useTabsSyncWithUrl from "../../../hooks/tabsSyncWithUrl";
import { WalletTable } from "../../../components/tables/WalletTable";
import { WalletFormButton } from "../../../components/WalletFormButton";
import { GenericInAppMessagesTable } from "../../../components/tables/InAppMessagesTable";
import InAppMessageListItem from "../../../models/InAppMessageListItem";

function AccountsTable({ userId, columnFilter = () => true, dataFilter = () => true }) {
  /// A function (account) => boolean, that is passed to filter() function
  /// to filter out which accounts to keep.
  // Similarly an optional function to filter which columns to keep
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filter, setFilter] = useState("");
  const { loading, loadMessage } = useContext(ToastContext);

   const navigate = useNavigate();
   const canNav = (record: Account) => {
    return [
      Account.types.COMPANY,
      Account.types.REAL_ESTATE,
    ].indexOf(record?.type) !== -1;
   }
   const gotoPage = (record: Account) => {
    if (record?.type === Account.types.COMPANY) {
      navigate(`/companies/${record.id}`);
    } else if (record?.type === Account.types.REAL_ESTATE) {
      navigate(`/realEstates/${record.id}`);
    }
   };
  function loadTable() {
    if (userId) {
      // Load all accounts for user
      loadMessage("Loading Accounts for user...", () =>
        Promise.all([AccountService.listAccountsOnUser(userId)]).then((data) => {
          const accounts = data[0].filter(dataFilter);
          setAccounts(accounts);
        })
      );
    } else {
      // Load all accounts
      loadMessage("Loading Accounts...", () =>
        Promise.all([AccountService.list()]).then((data) => {
          const accounts = data[0].filter(dataFilter);
          setAccounts(accounts);
        })
      );
    }
  }
  useEffect(() => {
    loadTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterTable = (data) => {
    const filterLc = filter.toLocaleLowerCase();
    return data.filter((item) => item.toString().toLocaleLowerCase().indexOf(filterLc) !== -1);
  };

  // Create columns for each property in data
  var columns: any = [
    {
      title: "Name",
      key: "name", // Used by UserProfile via columnFilter prop
      dataIndex: ["name"],
      render: (text, record, index) => {
        const hasNav = canNav(record);
        return <Typography.Text style={{ cursor: hasNav ? "pointer" : undefined }} onClick={hasNav ? () => gotoPage(record) : undefined}>
          {`${record?.name ?? ""}`.trim()}
        </Typography.Text>;
      },
      sorter: (a, b) => a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase()),
    },
    {
      title: "Type",
      key: "type",
      dataIndex: ["type"],
      render: (text, record, index) => {
        return `${Account.typeEmoji(record?.type)} ${record?.type}`;
      },
      sorter: (a, b) => a?.type?.toLowerCase().localeCompare(b?.type?.toLowerCase()),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (text, record, index) => {
        return `${record.createdAtAsDate}`;
      },
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record, index) => {
        return (
          <>
            {record.type !== Account.types.PRIVATE && (
              <WalletFormButton
                buttonLabel="Wallet"
                userId={userId}
                actorAccountId={record.id}
              />
            )}
          </>
        );
      },
    },
  ];
  // Filters
  const filterColumns = (columns = []) => columns.filter(columnFilter);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <Input.Search size="middle" placeholder="Search accounts..." style={{ width: 200 }} onChange={(e) => setFilter(e.target.value)} />
        </div>
      </div>
      <Table
        rowKey="id"
        columns={filterColumns(columns)}
        dataSource={filterTable(accounts)}
        pagination={{ total: accounts.length, defaultPageSize: 30, showSizeChanger: true }}
        // onRow={(record) => ({ onClick: () => gotoPage(record.id) })}
        loading={loading}
      />
      <Typography.Text style={{ position: "relative", top: -43, left: 10, color: "#bfbfbf" }}>Total of {accounts.length} accounts</Typography.Text>
    </div>
  );
}

function UserView(props) {
  let navigate = useNavigate();
  const gotoPage = (route) => {
    console.log("route is: " + route);
    navigate(route);
  };
  const { id } = useParams<string>();
  const userId = parseInt(id);
  const [user, setUser] = useState<User|null>(null);
  const [activeKey, onTabChange] = useTabsSyncWithUrl('profile');

  const { openModal, closeModal } = useContext(OverlayContext);
  const { loadMessage } = useContext(ToastContext);
  const [form] = Form.useForm();

  useEffect(() => {
    loadMessage("Loading user...", () =>
      Promise.all([UserService.getUser(id)]).then((data) => {
        setUser(data[0]);
        return true;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openUserUpdateModal = (user = null) => {
    openModal({
      title: "Update user",
      content: <UserForm id={userId} form={form} update={true} />,
      onOk: () => {
        form.validateFields().then(async (values) => {
          closeModal();
          loadMessage("Updating user", () =>
            UserService.editUser(
              values.id,
              _.pickBy(values, (x) => x !== null)
            )
              .then((user) => {
                setUser(user);
                return "User updated";
              })
              .catch((res) => {
                throw Error("Could not update user");
              })
          );
        });
      },
    });
  };
  // Filters
  const bookingDataFilter = (data: BookingTableData) => {
    return data.booking.userId === userId;
  };
  const inAppMessageFilter = (data: InAppMessageListItem) => {
    return data.toEmail === user?.username;
  };

  const handleEdit = () => {
    openUserUpdateModal(user);
  };
  const title = user ? user.firstName + " " + user.lastName : "User";
  const descriptionData = user ? {
    "username/email": user?.username,
    name: user?.firstName + " " + user?.lastName,
    "Personal Id": user?.personalId,
    address: user?.addressLine1,
    phoneNumber: user?.phoneNumber,
    zipCode: user?.zipCode,
    city: user?.city,
    role: user?.role,
    "user id": userId,
    "Bank-ID verified at": user['bankidVerifiedAt'] !== null && user['bankidVerifiedAt'] !== undefined
      ? formatDateTime(user['bankidVerifiedAt'])
      : 'Not yet verified',
    "app language": user?.langKey?.toLocaleUpperCase(),
  } : {};

  const tabItems: TabsProps["items"] = [
    {
      key: "profile",
      label: `User profile`,
      children: (
        <DescriptionDataList
          data={descriptionData}
          footer={
            <Button size="small" type="link" onClick={handleEdit}>
              Edit profile
            </Button>
          }
        />
      ),
    },
    {
      key: "wallet",
      label: `Wallet`,
      children: <WalletTable userId={userId} />,
    },
    {
      key: "bookings",
      label: `Bookings`,
      children: <BookingsTable dataFilter={bookingDataFilter} columnFilter={(col) => col.key !== "userId"}></BookingsTable>,
    },
    {
      key: "inAppMessages",
      label: `In-App-Messages`,
      children: <GenericInAppMessagesTable dataFilter={inAppMessageFilter} columnFilter={(col) => col.key !== "toEmail"}></GenericInAppMessagesTable>,
    },
    {
      key: "subscriptions",
      label: `Subscriptions`,
      children: <SubscriptionsTable params={{ userId }}></SubscriptionsTable>,
    },
    {
      key: "accounts",
      label: `Accounts`,
      children: <AccountsTable userId={userId}></AccountsTable>,
    },
  ];

  return (
    <div style={{ position: "relative" }}>
      <GoBackButton></GoBackButton>

      <ViewTitle title={title}></ViewTitle>
      <Tabs
        type="line"
        style={{ minHeight: "100vh" }}
        defaultActiveKey="users"
        items={user !== null ? tabItems : []}
        activeKey={activeKey}
        onChange={onTabChange} />
    </div>
  );
}

export default UserView;
